<template>
	<div class="blog-index-page">
		<HeadNav selectedLanguage="en" page="blog" />

		<header class="blog-header">
			<div class="container">
				<h1>VPN Blogs</h1>
				<p class="blog-intro">
					Latest VPN, privacy, streaming, online security, and internet-related articles,
					guides, and tutorials from SecureProxyVPN.
				</p>
			</div>
		</header>

		<main class="container main-content">
			<section aria-labelledby="latest-articles">
				<h2 id="latest-articles">Latest Articles</h2>

				<div class="blog-grid">
					<article v-for="blog in sortedBlogs" :key="blog.slug" class="blog-card">
						<router-link :to="blog.path" class="card-link" :aria-label="`Read ${blog.title}`">
							<div class="featured-image">
								<img :src="blog.poster" :alt="blog.h1" loading="lazy">
							</div>
							<div class="card-content">
								<h3>{{ blog.title }}</h3>
								<p>{{ blog.description }}</p>
								<div class="card-footer">
									<time :datetime="blog.date">{{ formatDate(blog.date) }}</time>
									<span class="read-more">
										Read More
										<svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
											<path
												d="M4 10h12M11 5l5 5-5 5"
												stroke="currentColor"
												stroke-width="1.8"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
									</span>
								</div>
							</div>
						</router-link>
					</article>
				</div>
			</section>
		</main>
	</div>
</template>

<script>
import '@/css/base.scss'
import '@/css/vpn-theme.scss'
import HeadNav from '@/components/HeadNav.vue'
import vpnBlogs from '@/documents/vpnBlogs'

export default {
	name: 'BlogIndex',
	components: {
		HeadNav,
	},
	computed: {
		sortedBlogs() {
			return [...vpnBlogs].sort((a, b) => new Date(b.date) - new Date(a.date))
		},
	},
	methods: {
		formatDate(date) {
			return new Date(date).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})
		},
	},
}
</script>

<style lang="scss" scoped>
.blog-index-page {
	min-height: 70vh;
	background:
		radial-gradient(ellipse 80% 50% at 10% -10%, rgba(91, 79, 232, 0.12), transparent 55%),
		radial-gradient(ellipse 60% 40% at 90% 0%, rgba(59, 105, 255, 0.08), transparent 50%),
		#f5f4fb;
	color: var(--vpn-ink);
	font-family: var(--vpn-font);
}

.container {
	width: var(--vpn-container);
	margin: 0 auto;
}

.blog-header {
	padding: 64px 0 26px;
	border-bottom: 1px solid rgba(91, 79, 232, 0.08);
}

.blog-header h1 {
	margin: 0;
	font-size: clamp(34px, 5vw, 48px);
	line-height: 1.12;
	font-weight: 800;
	letter-spacing: -0.03em;
	animation: vpn-rise 0.65s ease both;
}

.blog-intro {
	max-width: 820px;
	margin: 16px 0 0;
	font-size: 17px;
	line-height: 1.75;
	color: var(--vpn-ink-soft);
}

.main-content {
	padding: 44px 0 88px;
}

h2 {
	margin: 0 0 28px;
	font-size: clamp(24px, 3vw, 30px);
	line-height: 1.2;
	font-weight: 800;
	letter-spacing: -0.02em;
}

.blog-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 22px;
}

.blog-card {
	min-width: 0;
	animation: vpn-rise 0.7s ease both;
}

.blog-card:nth-child(2) {
	animation-delay: 0.06s;
}

.blog-card:nth-child(3) {
	animation-delay: 0.12s;
}

.card-link {
	display: flex;
	height: 100%;
	flex-direction: column;
	overflow: hidden;
	border: 1px solid var(--vpn-line);
	border-radius: 20px;
	background: #fff;
	color: inherit;
	text-decoration: none;
	box-shadow: var(--vpn-shadow-soft);
	transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.card-link:hover {
	transform: translateY(-4px);
	border-color: rgba(91, 79, 232, 0.22);
	box-shadow: 0 22px 48px rgba(67, 56, 202, 0.14);
}

.featured-image {
	aspect-ratio: 16 / 9;
	overflow: hidden;
	background: linear-gradient(135deg, #12122e, #2a2a6a);
}

.featured-image img {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
	transition: transform 0.45s ease;
}

.card-link:hover .featured-image img {
	transform: scale(1.04);
}

.card-content {
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: 24px 22px 22px;
}

.card-content h3 {
	margin: 0 0 12px;
	font-size: 20px;
	line-height: 1.38;
	font-weight: 800;
	letter-spacing: -0.02em;
	color: var(--vpn-ink);
}

.card-content p {
	flex: 1;
	margin: 0 0 22px;
	font-size: 15px;
	line-height: 1.7;
	color: var(--vpn-ink-soft);
}

.card-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding-top: 18px;
	border-top: 1px solid rgba(15, 48, 74, 0.07);
}

.card-footer time {
	font-size: 12px;
	font-weight: 700;
	color: var(--vpn-muted);
}

.read-more {
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	gap: 7px;
	font-size: 13px;
	font-weight: 800;
	color: var(--vpn-teal-deep);
}

.read-more svg {
	width: 15px;
	height: 15px;
	transition: transform 0.2s ease;
}

.card-link:hover .read-more svg {
	transform: translateX(3px);
}

@media (max-width: 900px) {
	.blog-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

@media (max-width: 620px) {
	.blog-header {
		padding: 48px 0 22px;
	}

	.main-content {
		padding: 36px 0 64px;
	}

	.blog-grid {
		grid-template-columns: 1fr;
	}
}
</style>
